import { useState } from 'react';

import Dropdown from '@componentLibrary/dropdown/Dropdown';
import Apps from '@mui/icons-material/Apps';
import ExitToApp from '@mui/icons-material/ExitToApp';
import GroupIcon from '@mui/icons-material/Group';
import Settings from '@mui/icons-material/Settings';
import SwapHoriz from '@mui/icons-material/SwapHoriz';

import EditUserProfileModal from '@components/overlays/EditUserProfileModal';
import SwitchAccountModal from '@components/overlays/SwitchAccountModal';

import { useApp, useControlCenterApp } from '@hooks/AppHooks';

import { mapReactFragmentChildrenToArray, openUrl } from '@helpers/ui';

import EmployeeNotificationBell from '@app/employee-app/features/notifications/components/EmployeeNotificationBell';
import { IAccount } from '@app/types/AccountsTypes';

import Avatar from '../../Avatar';
import DesktopHelpWidget from '../DesktopHelpWidget';
import NotificationWidget from '../NotificationWidget';

import './UserWidget.scss';

interface UserWidgetProps
    extends Pick<
        ReturnType<typeof useApp>,
        | 'user'
        | 'userIsAdmin'
        | 'userIsSuperAdmin'
        | 'userCanSwitchView'
        | 'refetchUserAppData'
        | 'isAdminApp'
        | 'appIsMobile'
    > {
    isControlCenterApp: boolean;
    account?: IAccount;
    onLogOut: () => void;
    goToControlCenter: () => void;
    goToEditProfile: () => void;
    switchUserViewFn: (show: boolean) => void;
}

export const UserWidget = ({
    isControlCenterApp,
    userIsAdmin,
    userIsSuperAdmin,
    userCanSwitchView,
    isAdminApp,
    user,
    account,
    appIsMobile,
    onLogOut,
    goToControlCenter,
    goToEditProfile,
    refetchUserAppData,
    switchUserViewFn,
}: UserWidgetProps) => {
    const [showSwitchAccountModal, setShowSwitchAccountModal] = useState(false);
    const [showEditUserProfileModal, setShowEditUserProfileModal] = useState(false);

    if (!user?._id) {
        return <></>;
    }

    const line1 = `${user.firstName} ${user.lastName}`;
    const line2 = account != null ? account.name : '--';

    const isEmployeeApp = !isAdminApp && !isControlCenterApp;
    // Admin & control-center keep the legacy popover-based NotificationWidget;
    // employees get a top-header bell that links to the dedicated notifications page.
    const showAdminNotificationWidget = !isControlCenterApp && isAdminApp;
    const showEmployeeNotificationBell = isEmployeeApp;
    const showAdminSupportWidget = !!account?.type;

    return (
        <div className="user-widget">
            {showAdminNotificationWidget && <NotificationWidget user={user} />}
            {showEmployeeNotificationBell && <EmployeeNotificationBell />}
            {showAdminSupportWidget && <DesktopHelpWidget accountType={account.type} />}
            <Dropdown>
                <Dropdown.Button
                    color="neutral"
                    variant="flat"
                    data-testid="user-menu-dropdown-button"
                >
                    <>
                        <div className="user-details">
                            <span className="user-name">{line1}</span>
                            <span className="user-location-title">
                                {user.role === 'super-admin' ? 'Super Admin' : line2}
                            </span>
                        </div>
                        {!appIsMobile && (
                            <Avatar firstName={user.firstName} src={user.avatarUrl || undefined} />
                        )}
                    </>
                </Dropdown.Button>
                <Dropdown.Menu>
                    {user.accounts != null && user?.accounts.length > 1 && (
                        <Dropdown.MenuItem
                            StartIcon={SwapHoriz}
                            onClick={() => setShowSwitchAccountModal(true)}
                        >
                            Switch Accounts
                        </Dropdown.MenuItem>
                    )}

                    {userIsAdmin &&
                        isAdminApp &&
                        mapReactFragmentChildrenToArray(
                            <>
                                {userIsSuperAdmin && (
                                    <Dropdown.MenuItem StartIcon={Apps} onClick={goToControlCenter}>
                                        Control Center
                                    </Dropdown.MenuItem>
                                )}
                                <Dropdown.MenuItem
                                    StartIcon={Settings}
                                    onClick={() => {
                                        if (account?._id == null) {
                                            setShowEditUserProfileModal(true);
                                        } else {
                                            goToEditProfile();
                                        }
                                    }}
                                >
                                    Edit Profile
                                </Dropdown.MenuItem>

                                <Dropdown.MenuDivider />
                            </>,
                        )}

                    {(userCanSwitchView || user.role === 'retailer-admin') && (
                        <Dropdown.MenuItem
                            StartIcon={GroupIcon}
                            onClick={() => switchUserViewFn(true)}
                        >
                            Switch User View
                        </Dropdown.MenuItem>
                    )}

                    <Dropdown.MenuItem StartIcon={ExitToApp} onClick={onLogOut}>
                        Log Out
                    </Dropdown.MenuItem>
                </Dropdown.Menu>
            </Dropdown>
            <SwitchAccountModal
                isVisible={showSwitchAccountModal}
                onClose={() => {
                    setShowSwitchAccountModal(false);
                }}
            />
            <EditUserProfileModal
                isVisible={showEditUserProfileModal}
                user={user}
                onClose={(wasSaved?: boolean) => {
                    if (wasSaved) {
                        requestAnimationFrame(() => {
                            refetchUserAppData();
                        });
                    }
                    setShowEditUserProfileModal(false);
                }}
            />
        </div>
    );
};

export default ({ account, switchUserViewFn }: { account?: IAccount; switchUserViewFn?: any }) => {
    const {
        history,
        logOut,
        user,
        userIsAdmin,
        userIsSuperAdmin,
        userCanSwitchView,
        refetchUserAppData,
        isAdminApp,
        appIsMobile,
    } = useApp();

    const { isControlCenterApp } = useControlCenterApp();

    const onLogOut = () => {
        // Employees (including those in the native WebView shell) must land on
        // /employee/login after logout; /login is the admin-only route.
        const postLogoutPath = isAdminApp || isControlCenterApp ? '/login' : '/employee/login';
        logOut().then(() => {
            history.push(postLogoutPath);
        });
    };

    const goToControlCenter = () => {
        if (isControlCenterApp) {
            history.push('/control-center');
        } else {
            openUrl('/control-center');
        }
    };

    const goToEditProfile = () => {
        history.push(`/${account?._id}/settings/profile/`);
    };

    const props = {
        user,
        userIsAdmin,
        userIsSuperAdmin,
        userCanSwitchView,
        refetchUserAppData,
        isAdminApp,
        appIsMobile,

        isControlCenterApp,
        account,
        onLogOut,
        goToControlCenter,
        goToEditProfile,
    };

    return <UserWidget {...props} switchUserViewFn={switchUserViewFn} />;
};
